import React from "react";
import { useT } from "@/lib/i18n";

/**
 * Faithful visual preview of `_build_signature_html` (see recruiter_api.py).
 * Uses the real JobGrid logo PNG (served from `/jobgrid-logo-banner.png`)
 * so the preview matches the exact banner recipients will see.
 *
 * When the affiliate has uploaded a logo and/or a display name, an
 * "Aanbevolen door <logo/name>" endorsement strip appears below the CTA.
 */
export default function BannerPreview({ logoDataUrl = "", displayName = "" }) {
  const { t } = useT();
  const hasEndorsement = Boolean(logoDataUrl) || Boolean((displayName || "").trim());
  return (
    <div className="border border-neutral-200 bg-neutral-50 p-6" data-testid="affiliate-banner-preview">
      <table cellPadding="0" cellSpacing="0" style={{
        borderCollapse: "collapse",
        fontFamily: "Georgia, 'Times New Roman', serif",
        background: "#F9F8F6",
        maxWidth: "540px",
        border: "1px solid #E5E1D7",
      }}>
        <tbody>
          <tr>
            {/* Gold accent strip */}
            <td style={{ width: "4px", backgroundColor: "#B08D3F", padding: 0, fontSize: "1px", lineHeight: "1px" }}>&nbsp;</td>
            <td style={{ padding: "20px 22px" }}>
              {/* JobGrid PNG wordmark wrapped in a white "sticker" so the
                  logo's white background reads as intentional, not accidental. */}
              <div style={{ display: "inline-block", background: "#FFFFFF", padding: "6px 12px", border: "1px solid #E5E1D7" }}>
                <img
                  src="/jobgrid-logo-banner.png"
                  alt="JobGrid — No. 1"
                  data-testid="banner-jobgrid-logo"
                  style={{ display: "block", maxHeight: "44px", width: "auto" }}
                />
              </div>
              {/* Value prop — clearer answer to WAT / WAAROM / OPGELOST */}
              <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "15px", color: "#1A1A1A", marginTop: "14px", lineHeight: 1.5 }}>
                <strong>Alles wat je nodig hebt voor je sollicitatie</strong>, in één rustige app.{" "}
                <span style={{ color: "#3D3934" }}>
                  Zoek vacatures, schrijf CV en motivatiebrieven met AI, en bereid je interview voor — op je eigen tempo.
                </span>
              </div>
              {/* Why-line */}
              <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic", fontSize: "13px", color: "#63605A", marginTop: "8px", lineHeight: 1.5 }}>
                Geen chaos meer met CV-versies en losse notities — alles op één plek, gratis om te starten.
              </div>
              {/* CTA with amber underline */}
              <div style={{ marginTop: "14px" }}>
                <a href="#" onClick={(e) => e.preventDefault()} style={{ fontFamily: "'Courier New', monospace", fontSize: "12px", fontWeight: 700, color: "#065F46", textDecoration: "none", letterSpacing: "1px", textTransform: "uppercase", borderBottom: "2px solid #B08D3F", paddingBottom: "2px" }}>
                  Maak een gratis account aan →
                </a>
              </div>
              {/* Optional "Aanbevolen door" endorsement — shows logo, name, or both. */}
              {hasEndorsement && (
                <div style={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid #E5E1D7" }} data-testid="affiliate-banner-endorsement">
                  <div style={{ fontFamily: "'Courier New', monospace", fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", color: "#78716C", marginBottom: "8px" }}>
                    {t("affLogoEndorsement")}
                  </div>
                  {logoDataUrl && (
                    <img
                      src={logoDataUrl}
                      alt={t("affLogoEndorsement")}
                      style={{ maxHeight: "36px", maxWidth: "220px", display: "block" }}
                    />
                  )}
                  {(displayName || "").trim() && (
                    <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "14px", color: "#1A1A1A", marginTop: logoDataUrl ? "4px" : "0" }}>
                      {displayName.trim()}
                    </div>
                  )}
                </div>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
